import { useCallback, useEffect, useRef, useState } from "react";
import { usePlatformCapabilities } from "@shared-core/capabilities/context";
import { useDataAdapter } from "@shared-core/data/DataAdapterContext";
import type { InsightsWallboardReconciliationResponse } from "@shared-core/data/api-types";
import {
  WallboardReconciliationView,
  type WallboardDrilldownState,
} from "./WallboardReconciliationView";

// The Wallboard Reconciliation tab: loads the composed
// /views/insights/wallboard-reconciliation measures + hero, and on tile click
// loads that measure's reconciling lines from
// /views/insights/wallboard-reconciliation/lines. Covers loading/error/empty,
// mirroring the other Insights tabs.

type State =
  | { status: "loading" }
  | { status: "error" }
  | { status: "ready"; data: InsightsWallboardReconciliationResponse };

export function WallboardReconciliationTab() {
  const adapter = useDataAdapter();
  const capabilities = usePlatformCapabilities();
  const [state, setState] = useState<State>({ status: "loading" });
  const [openMeasureKey, setOpenMeasureKey] = useState<string | null>(null);
  const [drilldown, setDrilldown] = useState<WallboardDrilldownState | null>(
    null,
  );
  const [isDownloadingCsv, setIsDownloadingCsv] = useState(false);
  // Guards against a slow drilldown response landing after the user has
  // toggled to a different measure (or closed the open one).
  const requestedKey = useRef<string | null>(null);

  const load = useCallback(async () => {
    setState({ status: "loading" });
    setOpenMeasureKey(null);
    setDrilldown(null);
    requestedKey.current = null;
    try {
      const data = await adapter.getInsightsWallboardReconciliation();
      setState({ status: "ready", data });
    } catch {
      setState({ status: "error" });
    }
  }, [adapter]);

  useEffect(() => {
    load();
  }, [load]);

  const toggleMeasure = useCallback(
    async (measureKey: string) => {
      if (openMeasureKey === measureKey) {
        setOpenMeasureKey(null);
        setDrilldown(null);
        requestedKey.current = null;
        return;
      }
      setOpenMeasureKey(measureKey);
      setDrilldown({ status: "loading" });
      requestedKey.current = measureKey;
      try {
        const lines = await adapter.getInsightsWallboardLines(measureKey);
        // Ignore a resolved response the user has since toggled away from.
        if (requestedKey.current === measureKey) {
          setDrilldown({ status: "ready", lines });
        }
      } catch {
        if (requestedKey.current === measureKey) {
          setDrilldown({ status: "error" });
        }
      }
    },
    [adapter, openMeasureKey],
  );

  const downloadCsv = useCallback(async () => {
    if (!openMeasureKey || isDownloadingCsv) return;
    setIsDownloadingCsv(true);
    try {
      const { filename, content, contentType } =
        await adapter.getInsightsWallboardLinesCsv(openMeasureKey);
      capabilities.downloadTextFile(filename, content, contentType);
    } catch {
      // A failed export is non-fatal — the on-screen table still stands. The
      // button re-enables so the user can retry.
    } finally {
      setIsDownloadingCsv(false);
    }
  }, [adapter, capabilities, openMeasureKey, isDownloadingCsv]);

  if (state.status === "loading") {
    return (
      <div className="border-border bg-surface text-muted-foreground flex min-h-[320px] items-center justify-center rounded-lg border text-sm">
        Loading reconciliation…
      </div>
    );
  }

  if (state.status === "error") {
    return (
      <div className="border-border bg-surface flex min-h-[320px] flex-col items-center justify-center gap-3 rounded-lg border text-center">
        <p className="text-destructive text-sm">
          Couldn’t load the reconciliation. Please try again.
        </p>
        <button
          type="button"
          onClick={load}
          className="text-primary text-sm underline hover:no-underline"
        >
          Retry
        </button>
      </div>
    );
  }

  return (
    <WallboardReconciliationView
      data={state.data}
      openMeasureKey={openMeasureKey}
      drilldown={openMeasureKey ? drilldown : null}
      onToggleMeasure={toggleMeasure}
      onDownloadCsv={downloadCsv}
      isDownloadingCsv={isDownloadingCsv}
    />
  );
}
